import { useId, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ChevronDown, Check } from "lucide-react";

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectFieldProps {
  label: string;
  value?: string[];
  options: MultiSelectOption[];
  onChange?: (value: string[]) => void;
  error?: string;
  placeholder?: string;
  className?: string;
  /** Hint visuel pour la validation EN PRÉPARATION (cf. lib/bienPrepValidation.ts). */
  missingHint?: "required" | "mailing";
}

export function MultiSelectField({
  label,
  value = [],
  options,
  onChange,
  error,
  placeholder = "...",
  className,
  missingHint,
}: MultiSelectFieldProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerId = useId();

  const isFilled = value.length > 0;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Si missingHint est actif ET le champ est encore vide → bordure colorée
  // pour signaler le manque (pattern aligné sur FormField).
  const showMissingBorder = !isFilled && !isOpen && !error && !!missingHint;
  const missingBorderClass =
    missingHint === "required"
      ? "border border-solid border-danger"
      : missingHint === "mailing"
        ? "border border-solid border-warning"
        : "";

  const borderClasses = error
    ? "border border-solid border-danger"
    : showMissingBorder
      ? missingBorderClass
      : isOpen
        ? "border border-solid border-border-focus"
        : isFilled && isHovered
          ? "border border-dashed border-border-hover"
          : isFilled
            ? "border border-solid border-transparent"
            : isHovered
              ? "border border-dashed border-border-hover"
              : "border border-dashed border-border";

  // Couleur du label : aligné sur FormField — required > mailing > muted.
  const labelColorClass = isFilled
    ? "text-text-muted"
    : missingHint === "required"
      ? "text-danger"
      : missingHint === "mailing"
        ? "text-warning"
        : "text-text-muted";

  const toggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange?.(newValue);
  };

  const displayText = isFilled
    ? value
        .map((v) => options.find((o) => o.value === v)?.label)
        .filter(Boolean)
        .join(", ")
    : "";

  return (
    <div className={cn("flex flex-col", className)} ref={ref}>
      <label htmlFor={triggerId} className={cn("text-xs font-medium tracking-wider uppercase px-3 mb-1", labelColorClass)}>
        {label}
      </label>
      <button
        id={triggerId}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={cn(
          "px-3 py-2 bg-surface-2 rounded-md text-sm text-left",
          "outline-none shadow-none transition-colors duration-150",
          "flex items-center justify-between gap-2 cursor-pointer",
          borderClasses,
          isFilled ? "text-text-primary" : "text-text-muted"
        )}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span className="truncate">{displayText || placeholder}</span>
        <ChevronDown
          size={16}
          className={cn(
            "text-text-muted transition-transform duration-150 shrink-0",
            isOpen && "rotate-180"
          )}
        />
      </button>
      {isOpen && (
        <div className="relative z-10">
          <div className="absolute top-1 left-0 right-0 bg-surface border border-border rounded-md shadow-lg max-h-[min(80vh,500px)] overflow-y-auto">
            {options.map((option) => {
              const isChecked = value.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  className={cn(
                    "w-full px-3 py-2 text-sm text-left flex items-center gap-3",
                    "hover:bg-gray-200 transition-colors cursor-pointer"
                  )}
                  onClick={() => toggleOption(option.value)}
                >
                  <div
                    className={cn(
                      "w-4 h-4 rounded-sm flex items-center justify-center shrink-0 transition-colors duration-150",
                      isChecked
                        ? "bg-accent"
                        : "border border-border bg-transparent"
                    )}
                  >
                    {isChecked && (
                      <Check size={12} className="text-white" strokeWidth={3} />
                    )}
                  </div>
                  <span className="text-text-primary">{option.label}</span>
                </button>
              );
            })}
            {/* Footer Tous / Aucun — cohérent avec CollabDropdown (suivi-els) */}
            <div className="border-t border-border flex gap-1 px-2 py-1 sticky bottom-0 bg-surface">
              <button
                type="button"
                onClick={() => onChange?.(options.map((o) => o.value))}
                className="flex-1 text-[11px] text-text-muted hover:text-accent py-1 cursor-pointer"
              >
                {t("common.selectAll")}
              </button>
              <span className="text-border self-center">|</span>
              <button
                type="button"
                onClick={() => onChange?.([])}
                className="flex-1 text-[11px] text-text-muted hover:text-accent py-1 cursor-pointer"
              >
                {t("common.none")}
              </button>
            </div>
          </div>
        </div>
      )}
      {error && (
        <span className="text-xs text-danger mt-1 px-3">{error}</span>
      )}
    </div>
  );
}
